/**
 * Execution utilities - shared helpers for execution/run mode detection
 */
import type * as T from "@src/types";
import type { ApiRunConfig } from "@tradingdroid/frontends-types";

/** Strategy config with known fields - extends base type with type safety */
export type StrategyConfig = NonNullable<ApiRunConfig["strategy_config"]> & {
  position_type?: string;
  leverage?: number | null;
};

/** Object with a config containing strategy_config (matches ApiRun, ApiExecution, etc.) */
interface HasStrategyConfig {
  config?: {
    strategy_config?: unknown;
  };
}

/** Helper to detect if execution/run is in futures mode from strategy_config */
export const isFuturesMode = (entity?: HasStrategyConfig): boolean => {
  const strategyConfig = entity?.config?.strategy_config as StrategyConfig | undefined;
  return strategyConfig?.position_type === "futures";
};

/** Read the configured leverage from strategy_config. Returns null if absent or invalid. */
export const getLeverage = (entity?: HasStrategyConfig): number | null => {
  const strategyConfig = entity?.config?.strategy_config as StrategyConfig | undefined;
  const lev = strategyConfig?.leverage;
  return typeof lev === "number" && lev > 0 ? lev : null;
};

export type FuturesDirection = "long" | "short";

export interface UnrealizedPnlParams {
  entryPrice: number;
  currentPrice: number;
  currentAmount: number;
  directionName?: string;
  typeName?: string;
  collateralIn: number | null;
}

/** Calculate unrealized PnL for an open position */
export const calculateUnrealizedPnl = (params: UnrealizedPnlParams): number => {
  const { entryPrice, currentPrice, currentAmount, directionName, typeName } = params;
  if (!entryPrice || !currentPrice || !currentAmount) return 0;

  // Return 0 if direction cannot be determined to avoid incorrect PnL calculation
  if (!directionName && !typeName) return 0;

  const isLong = directionName === "long" || typeName === "spot";
  return isLong
    ? (currentPrice - entryPrice) * currentAmount
    : (entryPrice - currentPrice) * currentAmount;
};

/** Calculate unrealized PnL % based on collateral */
export const calculateUnrealizedPnlPercent = (pnl: number, collateralIn: number | null): number => {
  if (pnl === 0 || !collateralIn) return 0;
  return (pnl / collateralIn) * 100;
};

/** Get latest price from TypedArrayCandles (datafeed props)
 * Note: Linear scan is intentional - prefer robustness over minor perf gain
 * since candles may not always be sorted chronologically */
export const getLatestPriceFromCandles = (candles: T.TypedArrayCandles | undefined): number => {
  if (!candles?.times?.length) return 0;
  let latestIdx = 0;
  let latestTime = candles.times[0];
  for (let i = 1; i < candles.times.length; i++) {
    if (candles.times[i] > latestTime) {
      latestTime = candles.times[i];
      latestIdx = i;
    }
  }
  return candles.closes[latestIdx] || 0;
};
